/**
 * Pure mapper: getCardState + observation fields → refresh card view (Task 12).
 * Does not call getCardState itself — caller passes precomputed CardState.
 * minimum_payment is observation-backed but not on CardState.observed; pass it in.
 */

import type { Cents } from '../../domain/money';
import type { CardState } from '../../domain/state';
import type { StateRefreshCardView } from './types';

export type BuildRefreshCardInput = {
  card_id: string;
  label: string;
  state: CardState;
  financial_staleness_days: number;
  /** From latest-non-null minimum_payment observation; null if never observed. */
  minimum_payment_cents: Cents | null;
  minimum_payment_observed_at: string | null;
};

/**
 * Build one refresh row. Missing observations stay null — never invent balances or dates.
 * is_stale when state_age_days exceeds the configured TTL.
 */
export function buildStateRefreshCard(input: BuildRefreshCardInput): StateRefreshCardView {
  const { state, financial_staleness_days } = input;
  const o = state.observed;
  const age = state.state_age_days;
  const isStale = age !== null && age > financial_staleness_days;

  return {
    card_id: input.card_id,
    label: input.label,
    current_balance: {
      previous_cents: o.current_balance_cents,
      observed_at: o.current_balance_cents_observed_at,
    },
    available_credit: {
      previous_cents: o.available_credit_cents,
      observed_at: o.available_credit_cents_observed_at,
    },
    credit_limit: {
      previous_cents: o.credit_limit_cents,
      observed_at: o.credit_limit_cents_observed_at,
    },
    statement_balance: {
      previous_cents: o.statement_balance_cents,
      observed_at: o.statement_balance_cents_observed_at,
    },
    minimum_payment: {
      previous_cents: input.minimum_payment_cents,
      observed_at: input.minimum_payment_observed_at,
    },
    payment_due_date: {
      previous: o.payment_due_date,
      observed_at: o.payment_due_date_observed_at,
    },
    estimated_posted_balance_cents: state.estimated_posted_balance,
    unreconciled_delta_cents: state.unreconciled_delta,
    state_age_days: age,
    is_stale: isStale,
    financial_staleness_days,
  };
}
